import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Client } from 'helpers/hooks/useEmployeeDetails';
import { KeyedMutator } from 'swr';
import { Alert, Skeleton, Snackbar, Stack, Typography } from '@mui/material';
import { Snackbar as CustomSnackbar } from '@atoms';
import { useModal, useSnackbar } from '@hooks';
import { RetrieveBalanceModal } from '@organisms';
import { Area } from '@modules/clients/modules/CompanyDetails/components/AreasList/AreasList';
import {
  employeeDetailsClick,
  EVENT_EMPLOYEE_DETAILS_CANCEL_RECOVER_BALANCE,
} from '@modules/employeeDetails/events';
import AssignAreaModal from './AssignAreaModal';
import { SNACKBAR_DURATION } from './constants';
import ProductCard from './ProductCard';
import styles from './styles';
import useAssignArea from './useAssignArea';
import { Product } from './useProducts';
import useRetriveBalance from './useRetriveBalance';

interface ProductsTabProps {
  client: Client;
  product: Product;
  isProductsLoading: boolean;
  productsMutate: KeyedMutator<Product[]>;
}

/** Component of all user products with their account  */
const ProductsTab = ({ client, product, isProductsLoading, productsMutate }: ProductsTabProps) => {
  const { t } = useTranslation();

  const { snackbarProps, showSnackbar } = useSnackbar();

  const {
    closeModal: closeRetrieveBalanceModal,
    data: productSelectedToRetrive,
    open: isRetrieveBalanceModalOpen,
    openModal: openRetrieveBalanceModal,
  } = useModal<Product>();

  const {
    closeModal: closeAssignAreaModal,
    data: customerAreas,
    open: isAssignAreaModalOpen,
    openModal: openAssignAreaModal,
  } = useModal<Area[]>();

  const { retrieveBalance, postRetrieveBalance, isAllowedToRetrieveBalance } = useRetriveBalance();
  const { error: errorRetrieve, loading: loadingRetrieve, updatedBalance } = retrieveBalance;

  const { isLoading: isAssignAreaLoading, postAssignArea } = useAssignArea();

  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [showFailAlert, setShowFailAlert] = useState(false);

  /** Handle calling of post retrieve balance  */
  const handlePostRetrieveBalance = (amount: number, fullRecovery: boolean) =>
    postRetrieveBalance({
      amount,
      fullRecovery,
      userAccountNumber: product.account_number,
    }).then(() => {
      productsMutate();
    });

  /**
   *
   */
  const handlePostAssignArea = async (area: string) =>
    postAssignArea({ area, accountId: product.account_id }).then(() => {
      closeAssignAreaModal();
      productsMutate();
    });

  useEffect(() => {
    if (updatedBalance && !errorRetrieve) {
      closeRetrieveBalanceModal();
      setShowSuccessAlert(true);
    }

    if (errorRetrieve) {
      closeRetrieveBalanceModal();
      setShowFailAlert(true);
    }
  }, [updatedBalance, errorRetrieve, closeRetrieveBalanceModal]);

  return (
    <>
      <Stack spacing={3}>
        {isProductsLoading ? (
          <Skeleton sx={styles.productSkeleton} variant='rectangular' />
        ) : (
          <ProductCard
            isAllowedToRetrieveBalance={isAllowedToRetrieveBalance}
            key={product.account_number}
            product={product}
            productsMutate={productsMutate}
            onPressRetrieveBalance={openRetrieveBalanceModal}
            onPressAssignArea={openAssignAreaModal}
            showSnackbar={showSnackbar}
            client={client}
          />
        )}
      </Stack>
      {productSelectedToRetrive && (
        <RetrieveBalanceModal
          submit={handlePostRetrieveBalance}
          isOpen={isRetrieveBalanceModalOpen}
          onClose={() => {
            closeRetrieveBalanceModal();
            employeeDetailsClick(EVENT_EMPLOYEE_DETAILS_CANCEL_RECOVER_BALANCE);
          }}
          isLoading={loadingRetrieve}
          totalBalance={product.balance_available}
        />
      )}
      {customerAreas && (
        <AssignAreaModal
          areas={customerAreas ?? []}
          postAssignArea={handlePostAssignArea}
          isOpen={isAssignAreaModalOpen}
          onClose={closeAssignAreaModal}
          isLoading={isAssignAreaLoading}
        />
      )}
      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={SNACKBAR_DURATION}
        onClose={() => setShowSuccessAlert(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert variant='filled' severity='success' onClose={() => setShowSuccessAlert(false)}>
          {t('EMPLOYEES:DETAIL:RETRIEVE_BALANCE_SUCCESS')}
        </Alert>
      </Snackbar>
      <Snackbar
        open={showFailAlert}
        autoHideDuration={SNACKBAR_DURATION}
        onClose={() => setShowFailAlert(false)}
      >
        <Alert variant='filled' severity='error' onClose={() => setShowFailAlert(false)}>
          <Typography variant='body2'>
            {t('EMPLOYEES:DETAIL:ERRORS:RETRIEVE_BALANCE_TITTLE')}
          </Typography>
          <Typography variant='body2'>
            {t('EMPLOYEES:DETAIL:ERRORS:RETRIEVE_BALANCE_CONTENT')}
          </Typography>
        </Alert>
      </Snackbar>
      <CustomSnackbar {...snackbarProps} />
    </>
  );
};

export default ProductsTab;
